import { Badge, Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import i18n from 'i18next';
import { TrendingDown, TrendingUp } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ScrollableTable from './ScrollableTable';
interface UsageHistoryTabProps {
  usageData: any;
}

// Catmull-Rom → cubic-bezier smoothing for a pleasant trend line (no chart lib).
const buildSmoothPath = (pts: [number, number][]): string => {
  if (pts.length < 2) return pts.length ? `M ${pts[0][0]} ${pts[0][1]}` : '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};

// Lightweight inline SVG area chart — "Questions — last 12 months" (mockup) with a
// gradient fill + trend %. Themed via CSS vars so it follows light/dark automatically.
const TrendChart: React.FC<{ title: string; values: number[]; pctLabel: string; up: boolean }> = ({
  title,
  values,
  pctLabel,
  up,
}) => {
  const W = 1000;
  const H = 160;
  const pad = 14;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const n = values.length;
  const pts: [number, number][] = values.map((v, i) => [
    n === 1 ? W / 2 : (i / (n - 1)) * W,
    H - pad - ((v - min) / span) * (H - 2 * pad),
  ]);
  const line = buildSmoothPath(pts);
  const area = line ? `${line} L ${pts[n - 1][0]} ${H} L ${pts[0][0]} ${H} Z` : '';

  return (
    <Box
      p={4}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='lg'
      shadow='ds_sm'
    >
      <Flex align='center' justify='space-between' mb={3}>
        <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
          {title}
        </Text>
        <Badge display='flex' variant={up ? 'ds-success' : 'ds-error'}>
          <HStack spacing={1}>
            <Icon as={up ? TrendingUp : TrendingDown} boxSize='12px' />
            <Text as='span'>{pctLabel}</Text>
          </HStack>
        </Badge>
      </Flex>
      <Box as='svg' w='100%' h='120px' preserveAspectRatio='none' viewBox={`0 0 ${W} ${H}`}>
        <defs>
          <linearGradient id='usageTrendFill' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='var(--chakra-colors-primary-solid)' stopOpacity='0.28' />
            <stop offset='100%' stopColor='var(--chakra-colors-primary-solid)' stopOpacity='0' />
          </linearGradient>
        </defs>
        {area && <path d={area} fill='url(#usageTrendFill)' />}
        {line && (
          <path
            d={line}
            fill='none'
            stroke='var(--chakra-colors-primary-solid)'
            strokeWidth='3'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        )}
      </Box>
    </Box>
  );
};

const UsageHistoryTab: React.FC<UsageHistoryTabProps> = ({ usageData }) => {
  const { t } = useTranslation();

  const colorText = 'text.default';

  const formatDate = (yearMonth: any) => {
    if (!yearMonth) {
      return 'N/A';
    }

    // Handle string format like "2025-08"
    if (typeof yearMonth === 'string') {
      const [year, month] = yearMonth.split('-').map(Number);
      const date = new Date(year, month - 1);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
      });
    }

    // Handle object format like { year: 2025, month: "AUGUST" }
    if (yearMonth.year && yearMonth.month) {
      const monthMap: any = {
        JANUARY: 0,
        FEBRUARY: 1,
        MARCH: 2,
        APRIL: 3,
        MAY: 4,
        JUNE: 5,
        JULY: 6,
        AUGUST: 7,
        SEPTEMBER: 8,
        OCTOBER: 9,
        NOVEMBER: 10,
        DECEMBER: 11,
      };
      const date = new Date(yearMonth.year, monthMap[yearMonth.month]);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
      });
    }

    return 'N/A';
  };

  // Sort data by date (newest first). Sort on RAW year_month (YYYY*100+MM), not the
  // localized formatDate() output (which breaks the YYYY-MM split → random order).
  const sortKey = (ym: any): number => {
    if (!ym) return 0;
    if (typeof ym === 'string') {
      const [y, m] = ym.split('-').map(Number);
      return (y || 0) * 100 + (m || 0);
    }
    if (ym.year && ym.month) {
      const M: any = {
        JANUARY: 1,
        FEBRUARY: 2,
        MARCH: 3,
        APRIL: 4,
        MAY: 5,
        JUNE: 6,
        JULY: 7,
        AUGUST: 8,
        SEPTEMBER: 9,
        OCTOBER: 10,
        NOVEMBER: 11,
        DECEMBER: 12,
      };
      return ym.year * 100 + (M[ym.month] || 0);
    }
    return 0;
  };
  const sortedData = React.useMemo(() => {
    const data = usageData?.monthly_summaries || [];
    return [...data].sort((a, b) => sortKey(b.year_month) - sortKey(a.year_month));
  }, [usageData]);

  // Chart series — last 12 months only (matches the title), chronological ascending
  // (oldest left), trend % = last vs first month in that window. sortedData is newest-first,
  // so take the first 12 then reverse. (#usage-trend-12mo)
  const chart = React.useMemo(() => {
    const asc = [...sortedData].slice(0, 12).reverse();
    const values = asc.map((d: any) => Number(d?.total_questions) || 0);
    const first = values.find((v) => v > 0) ?? values[0] ?? 0;
    const last = values[values.length - 1] ?? 0;
    const pct = first ? Math.round(((last - first) / first) * 100) : 0;
    return { values, pct };
  }, [sortedData]);

  return (
    <VStack align='stretch' h='90%' spacing={4}>
      {chart.values.length >= 2 && (
        <TrendChart
          title={t('managementSystem.companyDetail.questionsLast12', {
            defaultValue: 'Questions — last 12 months',
          })}
          values={chart.values}
          pctLabel={`${Math.abs(chart.pct)}%`}
          up={chart.pct >= 0}
        />
      )}
      <Box overflow='hidden' h='300px' borderRadius='lg'>
        <ScrollableTable
          columns={[
            {
              title: t('managementSystem.companyDetail.date'),
              dataIndex: 'year_month',
              key: 'year_month',
              width: '120px',
              render: (row: any) => <Text color={colorText}>{formatDate(row.year_month)}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.totalQuestions'),
              dataIndex: 'total_questions',
              key: 'total_questions',
              width: '140px',
              align: 'right',
              render: (row: any) => <Text color={colorText}>{row.total_questions}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.uploadedData'),
              dataIndex: 'total_storage_gb',
              key: 'total_storage_gb',
              width: '160px',
              align: 'right',
              render: (row: any) => (
                <Text color={colorText}>
                  {typeof row.total_storage_gb === 'number'
                    ? (() => {
                        const storageInMB = row.total_storage_gb * 1024;
                        return storageInMB % 1 === 0
                          ? `${storageInMB}`
                          : `${storageInMB.toFixed(2).replace(/\.?0+$/, '')}`;
                      })()
                    : row.total_storage_gb}
                </Text>
              ),
            },
            {
              title: t('managementSystem.companyDetail.totalTranslations'),
              dataIndex: 'total_translations',
              key: 'total_translations',
              width: '140px',
              align: 'right',
              render: (row: any) => <Text color={colorText}>{row.total_translations}</Text>,
            },
          ]}
          data={sortedData}
          rowKey='year_month'
          noDataText={t('managementSystem.companyDetail.noData')}
        />
      </Box>
    </VStack>
  );
};

export default UsageHistoryTab;
